import React from "react";
import { LDFlagSet, withLDConsumer } from "launchdarkly-react-client-sdk";
import Language from "../lib/Language";
import CalendarIcon from "./CalendarIcon";
import { Calendar } from "../models/Calendar";
import { EmptyHeader } from "../models/EmptyHeader";
import { ReservationSearch } from "../models/ReservationSearch";
import { OrderSearch } from "../models/OrderSearch";
import { WaitingListSearch } from "../models/WaitingListSearch";
import { RequestSearch } from "../models/RequestSearch";
import { CancellationSearch } from "../models/CancellationSearch";
import { ConflictSearch } from "../models/ConflictSearch";
import _ from "underscore";

// Header Titles
import { DateHeader } from "../models/DateHeader";
import { TimeHeader } from "../models/TimeHeader";
import { ObjectHeader } from "../models/ObjectHeader";
import { WeekHeader } from "../models/WeekHeader";
import { WeekdayHeader } from "../models/WeekdayHeader";
// Input types
import IntegerInput from "./IntegerInput";
import BooleanInput from "./BooleanInput";
import DateInput from "./DateInput";
import TimeInput from "./TimeInput";
import TimeRangeInput from "./TimeRangeInput";
import ArrayInput from "./ArrayInput";
import DateOffsetInput from "./DateOffsetInput";
import GroupInput from "./GroupInput";
import TypeInput from "./TypeInput";
import ColorTypeInput from "./ColorTypeInput";
import PeriodTypeInput from "./PeriodTypeInput";
import { TAppContextProp, withAppContext } from "../contexts/AppContext";
import { TComponentType } from "../models/Section";
import { Selection } from "../models/Selection";

type TSettingsProps = {
    /** The component whose settings are shown; nothing is shown without one. */
    calendar?: TComponentType;
    publicSelection: Selection;
    getCalendarIndex: (calendar: TComponentType) => number;
    flags?: LDFlagSet;
} & TAppContextProp;

class Settings extends React.Component<TSettingsProps> {
    onHeaderUpdate = (header, settingsObject, data) => {
        const newHeader = settingsObject.set(data);
        this.props.appContext.update(header, newHeader);
    };

    onCalendarUpdate = (settingsObject, data) => {
        this.props.appContext.update(this.props.calendar, settingsObject.set(data));
    };

    /**
     * Every component model answers its settings, but they do not agree on what
     * they are asked: only the calendar reads the flags, three of the searches
     * read the public selection, and the rest take neither.
     */
    getComponentSettings = (component: TComponentType) => {
        if (component instanceof Calendar) {
            return component.getSettings(this.props.publicSelection, this.props.flags);
        }
        if (
            component instanceof ReservationSearch ||
            component instanceof WaitingListSearch ||
            component instanceof RequestSearch
        ) {
            return component.getSettings(this.props.publicSelection);
        }
        return component.getSettings();
    };

    getHeaderTitle = (header) => {
        if (header instanceof DateHeader) {
            return Language.get("cal_header_kind_date");
        }
        if (header instanceof TimeHeader) {
            return Language.get("cal_header_kind_time");
        }
        if (header instanceof ObjectHeader) {
            return Language.get("cal_header_kind_object");
        }
        if (header instanceof WeekHeader) {
            return Language.get("cal_header_kind_week");
        }
        if (header instanceof WeekdayHeader) {
            return Language.get("cal_header_kind_weekday");
        }

        return Language.get("cal_header_kind_period");
    };

    getSettingsComponent = (settings, onUpdate) => {
        if (settings.visible === false) {
            return null;
        }

        let input;
        let InputType;
        const disabled = _.isFunction(settings.isDisabled) ? settings.isDisabled() : false;
        if (settings.type === "integer") {
            InputType = IntegerInput;
            input = <InputType defaultValue={settings.get()} onUpdate={onUpdate} />;
        } else if (settings.type === "boolean") {
            InputType = BooleanInput;
            input = (
                <InputType defaultValue={settings.get()} onUpdate={onUpdate} disabled={disabled} />
            );
        } else if (settings.type === "date") {
            InputType = DateInput;
            input = (
                <InputType defaultValue={settings.get()} onUpdate={onUpdate} disabled={disabled} />
            );
        } else if (settings.type === "time") {
            InputType = TimeInput;
            input = <InputType defaultValue={settings.get()} onUpdate={onUpdate} />;
        } else if (settings.type === "timerange") {
            InputType = TimeRangeInput;
            input = <InputType defaultValue={settings.get()} onUpdate={onUpdate} />;
        } else if (settings.type === "array") {
            InputType = ArrayInput;
            input = (
                <InputType
                    defaultValue={settings.get()}
                    onUpdate={onUpdate}
                    limit={settings.limit}
                    defaultSize={7}
                />
            );
        } else if (settings.type === "dateoffset") {
            InputType = DateOffsetInput;
            input = <InputType defaultValue={settings.get()} onUpdate={onUpdate} />;
        } else if (settings.type === "groups") {
            InputType = GroupInput;
            input = (
                <InputType
                    defaultValue={settings.get()}
                    onUpdate={onUpdate}
                    availableValues={settings.availableValues()}
                />
            );
        } else if (settings.type === "type") {
            InputType = TypeInput;
            input = <InputType defaultValue={settings.get()} onUpdate={onUpdate} />;
        } else if (settings.type === "colortype") {
            InputType = ColorTypeInput;
            input = (
                <InputType size={1} limit={1} defaultValue={settings.get()} onUpdate={onUpdate} />
            );
        } else if (settings.type === "periodtype") {
            InputType = PeriodTypeInput;
            input = (
                <InputType size={1} limit={1} defaultValue={settings.get()} onUpdate={onUpdate} />
            );
        }

        return (
            <li key={settings.id}>
                <label>
                    <span title={settings.details || settings.label} className={"settingsLabel"}>
                        {settings.label}
                    </span>
                    {input}
                </label>
            </li>
        );
    };

    getCalendarSettings = () => {
        const booleanItems: boolean[] = [];
        const otherItems: any[] = [];
        const calendar = this.props.calendar;
        if (!calendar) {
            return null;
        }
        this.getComponentSettings(calendar).items.forEach((item) => {
            if (item.type === "boolean") {
                booleanItems.push(item);
            } else {
                otherItems.push(item);
            }
        });

        const mapFunction = (setting) => {
            const update = this.onCalendarUpdate.bind(this, setting);
            return this.getSettingsComponent(setting, update);
        };

        return (
            <form>
                <ul className="checkboxGroup">{booleanItems.map(mapFunction, this)}</ul>
                <ul>{otherItems.map(mapFunction, this)}</ul>
            </form>
        );
    };

    getHeaderSettings = (topHeader) => {
        // Only a calendar has headers, and render checks that before asking.
        const calendar = this.props.calendar;
        if (!(calendar instanceof Calendar)) {
            return [];
        }
        const providers = calendar.getProviderMap();

        if (topHeader instanceof EmptyHeader) {
            return [];
        }

        return topHeader.getHeaders().map((header, index) => {
            const booleanItems: boolean[] = [];
            const otherItems: any[] = [];
            header
                .getSettings(providers, this.props.appContext.customWeekNames)
                .items.forEach((item) => {
                    if (item.type === "boolean") {
                        booleanItems.push(item);
                    } else {
                        otherItems.push(item);
                    }
                });

            const componentMap = (setting) => {
                const update = this.onHeaderUpdate.bind(this, header, setting);
                return this.getSettingsComponent(setting, update);
            };

            const title = this.getHeaderTitle(header);
            return [
                <h4 key={`header${index}`}>
                    {Language.get("nc_calendar_settings_header")} {index + 1}{" "}
                    {title ? `- ${title}` : ""}
                </h4>,
                <form key={`form${index}`}>
                    <ul className="checkboxGroup">{booleanItems.map(componentMap, this)}</ul>
                    <ul>{otherItems.map(componentMap, this)}</ul>
                </form>,
            ];
        }, this);
    };

    render() {
        if (!this.props.calendar) {
            return <div />;
        }
        const id = this.props.getCalendarIndex(this.props.calendar) + 1;
        const icon = <CalendarIcon calendar={this.props.calendar} size={16} isActive={true} />;
        const lists = [
            {
                type: ReservationSearch,
                title: Language.get("nc_cal_header_topleft_to_reservation_list"),
            },
            { type: OrderSearch, title: Language.get("nc_cal_header_topleft_to_order_list") },
            {
                type: WaitingListSearch,
                title: Language.get("dynamic_reserv_list_reserv_wl_wl_title"),
            },
            { type: CancellationSearch, title: Language.get("nc_cancellation_list_title") },
            { type: RequestSearch, title: Language.get("nc_request_list_title") },
            { type: ConflictSearch, title: Language.get("nc_conflict_list") },
        ];

        const listItem = _.find(lists, (list) => this.props.calendar instanceof list.type);
        if (listItem) {
            return (
                <div className="settingsPane">
                    <h2>{Language.get("cal_res_side_tab_view_prefs")}</h2>
                    <h3>
                        {icon} {listItem.title} {id}
                    </h3>
                    {this.getCalendarSettings()}
                </div>
            );
        }

        if (!(this.props.calendar instanceof Calendar)) {
            return (
                <div className="settingsPane">
                    <h2>{Language.get("cal_res_side_tab_view_prefs")}</h2>
                    <p>{Language.get("nc_menu_calendar_settings_no_calendar")}</p>
                </div>
            );
        }

        const xHeaderSettings = this.getHeaderSettings(this.props.calendar.xHeader);
        const yHeaderSettings = this.getHeaderSettings(this.props.calendar.yHeader);
        if (xHeaderSettings.length > 0) {
            xHeaderSettings.unshift(
                <h3 key={"xHeadline"}>{Language.get("nc_calendar_settings_x_axis")}</h3>
            );
        }
        if (yHeaderSettings.length > 0) {
            yHeaderSettings.unshift(
                <h3 key={"yHeadline"}>{Language.get("nc_calendar_settings_y_axis")}</h3>
            );
        }

        return (
            <div className="settingsPane" key={this.props.calendar._modelId}>
                <h2>{Language.get("cal_res_side_tab_view_prefs")}</h2>
                <h3>
                    {icon} {Language.get("menu_calendar")} {id}
                </h3>
                {this.getCalendarSettings()}
                {xHeaderSettings}
                {yHeaderSettings}
            </div>
        );
    }

    shouldComponentUpdate(nextProps) {
        return (
            this.props.calendar !== nextProps.calendar ||
            this.props.flags?.coreEditableTypeFilterCalendars !==
                nextProps.flags?.coreEditableTypeFilterCalendars
        );
    }
}

export default withLDConsumer()(withAppContext(Settings));
